// --- START OF FILE /potree-project/src/PotreeViewer.jsx ---

import { useEffect, useRef } from "react";
import config from "./config";

export default function PotreeViewer({ onViewerLoaded, setIsLoading }) {
    const containerRef = useRef(null);
    const renderAreaRef = useRef(null);
    
    // We use a ref to keep the latest callback without triggering the effect again
    const onViewerLoadedRef = useRef(onViewerLoaded);

    useEffect(() => {
        onViewerLoadedRef.current = onViewerLoaded;
    }, [onViewerLoaded]);

    useEffect(() => {
        let cancelled = false;

        const loadStyle = (href) =>
            new Promise((resolve, reject) => {
                if (document.querySelector(`link[href="${href}"]`)) return resolve();
                const link = document.createElement("link");
                link.rel = "stylesheet";
                link.href = href;
                link.onload = resolve;
                link.onerror = () => reject(new Error(`Failed to load CSS ${href}`));
                document.head.appendChild(link);
            });

        const loadScript = (src, isModule = false, globalName = null) =>
            new Promise((resolve, reject) => {
                // If script exists, we assume it's loading or loaded. 
                // Note: In a production app, robust loading might check readyState, 
                // but for Potree this is usually sufficient.
                if (document.querySelector(`script[src="${src}"]`)) return resolve();

                const s = document.createElement("script");

                if (isModule) {
                    s.type = "module";
                    s.innerHTML = `
                        import * as module from '${src}';
                        window.${globalName} = module.default || module;
                    `;
                    document.body.appendChild(s);
                    setTimeout(resolve, 50);
                } else {
                    s.src = src;
                    s.async = false;
                    s.onload = resolve;
                    s.onerror = () => reject(new Error(`Failed to load script ${src}`));
                    document.body.appendChild(s);
                }
            });

        async function boot() {
            try {
                await loadStyle("/potree/potree.css");
                await loadStyle("/potree/libs/jquery-ui/jquery-ui.min.css");
                await loadStyle("/potree/libs/openlayers3/ol.css");
                await loadStyle("/potree/libs/spectrum/spectrum.css");
                await loadStyle("/potree/libs/jstree/themes/mixed/style.css");

                await loadScript("/potree/libs/jquery/jquery-3.1.1.min.js");
                await loadScript("/potree/libs/json5-2.1.3/json5.mjs", true, "JSON5");
                await loadScript("/potree/libs/spectrum/spectrum.js");
                await loadScript("/potree/libs/jquery-ui/jquery-ui.min.js");
                await loadScript("/potree/libs/other/BinaryHeap.js");
                await loadScript("/potree/libs/tween/tween.min.js");
                await loadScript("/potree/libs/d3/d3.js");
                await loadScript("/potree/libs/proj4/proj4.js");
                await loadScript("/potree/libs/openlayers3/ol.js");
                await loadScript("/potree/libs/i18next/i18next.js");
                await loadScript("/potree/libs/jstree/jstree.js");
                await loadScript("/potree/potree.js");
                await loadScript("/potree/libs/plasio/js/laslaz.js");

                if (cancelled) return;

                // Safety check: ensure Potree is actually available on window
                if (!window.Potree) {
                    console.error("Potree script loaded but window.Potree is undefined. Retrying...");
                    // Simple retry logic could go here, or just fail gracefully
                    return;
                }

                const Potree = window.Potree;
                Potree.scriptPath = `${window.location.origin}/potree`;

                if (!renderAreaRef.current) return;

                const viewer = new Potree.Viewer(renderAreaRef.current);

                viewer.setEDLEnabled(true);
                viewer.setEDLStrength(0.0);
                viewer.setFOV(60);
                viewer.setPointBudget(5_000_000);
                viewer.setMoveSpeed(400.0);
                viewer.setClipTask(Potree.ClipTask.SHOW_OUTSIDE);

                if (viewer.orbitControls) viewer.setControls(viewer.orbitControls);
                if (viewer.compass) viewer.compass.setVisible(false);

                viewer.loadSettingsFromURL?.();

                const urlParams = new URLSearchParams(window.location.search);
                const pointCloudPath = urlParams.get('pointcloud');
                const savePath = urlParams.get('savePath');

                // Load the GUI and then trigger the callback via REF
                viewer.loadGUI().then(() => {
                    console.log("Potree GUI loaded");
                    if (onViewerLoadedRef.current) {
                        onViewerLoadedRef.current(viewer, savePath);
                    }
                });

                let resourcePath, resourceName;
                if (pointCloudPath) {
                    console.log(`Loading point cloud from URL parameter: ${pointCloudPath}`);
                    resourcePath = "https://www.alpicapture.com/accloud/pointclouds/" + pointCloudPath + "/metadata.json";
                    resourceName = pointCloudPath.split('/').pop();
                } else {
                    console.log("No point cloud in URL, loading default model.");
                    resourcePath = "/pointclouds/vol_total/cloud.js";
                    resourceName = "Model";
                }

                Potree.loadPointCloud(resourcePath, resourceName, (e) => {
                    const scene = viewer.scene;
                    const pointcloud = e.pointcloud;
                    const material = pointcloud.material;

                    material.minSize = 2;
                    material.size = 0.35;
                    material.pointSizeType = Potree.PointSizeType.ADAPTIVE;
                    material.shape = Potree.PointShape.CIRCLE;

                    scene.addPointCloud(pointcloud);
                    viewer.fitToScreen();

                    if (savePath) {
                        const savedViewUrl = `${config.API_BASE_URL}${savePath.endsWith('/') ? savePath : `${savePath}/`}viewerDataSave.json5`;                        console.log(`Attempting to load saved view from: ${savedViewUrl}`);
                        setIsLoading(true);

                        fetch(savedViewUrl)
                            .then(response => {
                                if (response.ok) return response.text();
                                if (response.status === 404) {
                                    console.log("No saved view file found. Using default scene setup.");
                                    return null;
                                }
                                throw new Error(`Server responded with status: ${response.status}`);
                            })
                            .then(json5Content => {
                                if (json5Content) {
                                    const projectData = window.JSON5.parse(json5Content);
                                    Potree.loadProject(viewer, projectData);
                                    console.log("Successfully applied saved view from drive.");
                                }
                            })
                            .catch(error => console.error("Could not load or apply saved view:", error))
                            .finally(() => {
                                setIsLoading(false);
                            });
                    }
                });
            } catch (err) {
                console.error(err);
            }
        }

        boot();

        return () => { 
            cancelled = true; 
            // Optional: cleanup Potree viewer here if necessary, 
            // e.g. renderAreaRef.current.innerHTML = ''; 
        };
    }, []); // Empty dependency array: Run once on mount

    return (
        <div
            ref={containerRef}
            className="potree_container"
            style={{ position: "absolute", width: "100%", height: "100%", left: 0, top: 0 }}
        >
            <div
                id="potree_render_area"
                ref={renderAreaRef}
                style={{ width: "100%", height: "100%" }}
            />
            <div id="potree_sidebar_container" />
        </div>
    );
}